import { useState, useEffect } from 'react'

interface SSNInputProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

/**
 * SSN Input component that formats as xxx-xx-xxxx 
 * Allows typing all 9 digits, then formats to show only last 4 digits
 */
export function SSNInput({
  value,
  onChange,
  disabled = false,
  placeholder = 'xxx-xx-xxxx ',
  className = '',
}: SSNInputProps) {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  // Extract digits from value
  const digits = value ? value.replace(/\D/g, '') : ''

  useEffect(() => {
    // When focused or less than 9 digits, show what user is typing
    if (isFocused || digits.length < 9) {
      if (digits.length === 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync the masked display to the value prop / focus state
        setDisplayValue('')
      } else if (digits.length <= 3) {
        // Show first 3 digits: 123
        setDisplayValue(digits)
      } else if (digits.length <= 5) {
        // Show first 5 digits: 123-45
        setDisplayValue(`${digits.slice(0, 3)}-${digits.slice(3)}`)
      } else {
        // Show all digits: 123-45-6789
        setDisplayValue(`${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`)
      }
    } else {
      // When not focused and has 9 digits, show masked format with last 4: XXX-XX-1234
      const lastFour = digits.slice(-4)
      setDisplayValue(`XXX-XX-${lastFour}`)
    }
  }, [digits, isFocused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value

    // Remove all non-digits
    const newDigits = input.replace(/\D/g, '')

    // Limit to 9 digits
    const limitedDigits = newDigits.slice(0, 9)

    // Update the actual value (store full 9 digits)
    onChange(limitedDigits)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  return (
    <input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={11} // xxx-xx-xxxx  = 11 characters max
      className={className}
    />
  )
}
